// views/Dashboard/Profile/components/CasoDetailView.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Button,
  Spinner,
  Icon,
  Portal,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink
} from '@chakra-ui/react';
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  Calendar,
  RefreshCw,
  Eye,
  Plus,
  ChevronLeft,
  FileText
} from 'lucide-react';
import { casosService, casosUtils } from 'services/casosService';
import FichasDisponiblesColumn from './FichasDisponiblesColumn';
import ModalSurveyResponse from "components/Modal/ModalSurveyResponse";
import { getSurveyResponse } from "services/surveyService";

const STATUS_STYLE = {
  abierto: { bg: 'mint.bg', ink: 'mint.ink' },
  pausado: { bg: 'butter.bg', ink: 'butter.ink' },
  cerrado: { bg: 'cream.bg2', ink: 'ink.400' },
};

const STAGE_PASTEL = {
  recepcion: 'sky',
  investigacion: 'lav',
  resolucion: 'butter',
  cierre: 'mint',
};

const CasoDetailView = ({ caso, customer, onBack, onFichaClick, onRefresh, onAddFicha, availableCaseForms = [] }) => {
  // Estados
  const [workflowData, setWorkflowData] = useState({});
  const [fichasDisponibles, setFichasDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFichas, setLoadingFichas] = useState(false);
  const [error, setError] = useState(null);
  const [draggedFicha, setDraggedFicha] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isOpenSurveyResponse, setIsOpenSurveyResponse] = useState(false);
  const [surveyModalData, setSurveyModalData] = useState(null);

  const toast = useToast();

  // Validación temprana - evitar renderizar si no hay caso válido
  if (!caso || !caso.id) {
    return (
      <Box w="100%" p={6} fontFamily="jakarta">
        <Box bg="butter.bg" borderRadius="14px" p={4}>
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="700" fontSize="13.5px" color="butter.ink">Caso no encontrado</Text>
            <Text fontSize="12.5px" color="butter.ink">
              No se pudo cargar la información del caso. Intenta seleccionar un caso válido.
            </Text>
          </VStack>
        </Box>
        {onBack && (
          <Button
            mt={4} size="sm" variant="ghost" color="coral.500"
            leftIcon={<Icon as={ChevronLeft} />}
            onClick={onBack}
          >
            Volver a casos
          </Button>
        )}
      </Box>
    );
  }

  /**
   * Cargar fichas del caso específico
   */
  const loadFichasDelCaso = useCallback(async () => {
    if (!caso?.id) {
      console.warn('⚠️ CasoDetailView - No se proporcionó ID de caso');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔍 CasoDetailView - Cargando fichas del caso:', caso.id);

      const response = await casosService.getFichasByCaso(caso.id);

      if (response.success) {
        setWorkflowData(response.data);
        console.log('✅ CasoDetailView - Fichas cargadas:', Object.keys(response.data).length, 'stages');
      } else {
        throw new Error(response.message || 'Error al cargar fichas del caso');
      }

    } catch (err) {
      console.error('❌ CasoDetailView - Error cargando fichas:', err);
      setError(err.message || 'Error al cargar fichas del caso');
    } finally {
      setLoading(false);
    }
  }, [caso?.id]);

  /**
   * Cargar fichas disponibles del usuario (no asignadas a casos)
   */
  const loadFichasDisponibles = useCallback(async () => {
    if (!customer?.username) {
      console.warn('⚠️ CasoDetailView - No se proporcionó username del customer');
      return;
    }

    try {
      setLoadingFichas(true);

      console.log('🔍 CasoDetailView - Cargando fichas disponibles del usuario:', customer.username);

      const response = await casosService.getUserSurveyResponses(customer.username);

      if (response.success) {
        // Filtrar solo las fichas disponibles (sin caso asignado)
        const fichasDisponibles = response.data.filter(ficha => ficha.is_available);
        setFichasDisponibles(fichasDisponibles);
        console.log('✅ CasoDetailView - Fichas disponibles cargadas:', fichasDisponibles.length);
      } else {
        throw new Error(response.message || 'Error al cargar fichas disponibles');
      }

    } catch (err) {
      console.error('❌ CasoDetailView - Error cargando fichas disponibles:', err);
      // No marcamos error general por las fichas disponibles
    } finally {
      setLoadingFichas(false);
    }
  }, [customer?.username]);

  // Cargar datos al montar el componente
  useEffect(() => {
    if (caso?.id) {
      loadFichasDelCaso();
    }
    if (customer?.username) {
      loadFichasDisponibles();
    }
  }, [caso?.id, customer?.username, loadFichasDelCaso, loadFichasDisponibles]);

  /**
   * Actualizar stage del caso (actualización optimista + persistencia)
   */
  const updateCaseStage = async (caseId, fromStage, toStage, comment = null) => {
    try {
      // 1. Actualizar estado local optimísticamente PRIMERO
      setWorkflowData(prevData => {
        const newData = { ...prevData };
        
        // Encontrar el caso y moverlo
        const caseToMove = newData[fromStage]?.cases.find(c => c.id === caseId);
        if (caseToMove) {
          // Remover de la columna origen
          newData[fromStage] = {
            ...newData[fromStage],
            cases: newData[fromStage].cases.filter(c => c.id !== caseId)
          };
          
          // Actualizar caso con nuevo stage
          const updatedCase = {
            ...caseToMove,
            workflow_stage: toStage,
            stage_name: casosUtils.getStageName(toStage),
            stage_color: casosUtils.getStageColor(toStage),
            stage_updated_at: new Date().toISOString(),
            workflow_metadata: {
              ...caseToMove.workflow_metadata,
              history: [
                ...(caseToMove.workflow_metadata?.history || []),
                {
                  from_stage: fromStage,
                  to_stage: toStage,
                  updated_by: 1, // TODO: obtener usuario actual
                  updated_at: new Date().toISOString(),
                  comment: comment
                }
              ]
            }
          };
          
          // Agregar a la columna destino
          newData[toStage] = {
            ...newData[toStage],
            cases: [updatedCase, ...newData[toStage].cases]
          };
        }
        
        return newData;
      });

      // 2. Mostrar toast inmediatamente
      toast({
        title: 'Caso actualizado',
        description: `Caso movido a ${casosUtils.getStageName(toStage)}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      // 3. Persistir cambio en el backend de forma asíncrona
      try {
        // TODO: Implementar actualización de stage en el backend API
        // await casosService.updateCasoStage(caseId, toStage, comment, 1);
        console.log(`✅ Stage actualizado en backend: ${caseId} -> ${toStage}`);

        // NO llamar updateSurveyResponses() para evitar recarga completa
        // El estado local ya está actualizado y es suficiente

      } catch (apiError) {
        console.warn('⚠️ Error persistiendo en backend (cambio local mantenido):', apiError.message);
        // No revertimos el cambio local porque la UX debe ser fluida
        // Se podría agregar un indicador visual de "pendiente de sincronizar"
      }

    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Error al actualizar caso',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      
      // Revertir cambios en caso de error
      loadFichasDelCaso();
      loadFichasDisponibles();
    }
  };

  /**
   * Handlers de drag & drop
   */
  const handleDragStart = (e, ficha) => {
    setDraggedFicha(ficha);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(stage);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedFicha) {
      return;
    }

    try {
      // Si la ficha viene de "disponible", la adjuntamos al caso
      if (draggedFicha.is_available && targetStage !== 'disponible') {
        console.log('🔗 Adjuntando ficha disponible al caso:', {
          fichaId: draggedFicha.id,
          casoId: caso.id,
          targetStage
        });

        const response = await casosService.attachFichaToCaso(
          caso.id,
          draggedFicha.id,
          targetStage
        );

        if (response.success) {
          toast({
            title: 'Ficha adjuntada',
            description: `Ficha adjuntada al caso en ${casosUtils.getStageName(targetStage)}`,
            status: 'success',
            duration: 2000,
            isClosable: true
          });

          // Actualizar estado local optimísticamente
          setFichasDisponibles(prev => prev.filter(f => f.id !== draggedFicha.id));

          // Agregar a fichas del caso
          setWorkflowData(prevData => {
            const newData = { ...prevData };
            const updatedFicha = {
              ...draggedFicha,
              workflow_stage: targetStage,
              stage_name: casosUtils.getStageName(targetStage),
              stage_updated_at: new Date().toISOString(),
              is_available: false
            };

            if (newData[targetStage]) {
              newData[targetStage] = {
                ...newData[targetStage],
                cases: [updatedFicha, ...newData[targetStage].cases]
              };
            }

            return newData;
          });
        }
      }
      // Si la ficha se mueve desde el workflow a "disponible", la desadjuntamos
      else if (!draggedFicha.is_available && targetStage === 'disponible') {
        console.log('🔗 Desadjuntando ficha del caso:', {
          fichaId: draggedFicha.id,
          casoId: caso.id
        });

        const response = await casosService.detachFichaFromCaso(
          caso.id,
          draggedFicha.id
        );

        if (response.success) {
          toast({
            title: 'Ficha desadjuntada',
            description: 'Ficha devuelta a fichas disponibles',
            status: 'info',
            duration: 2000,
            isClosable: true
          });

          // Actualizar estado local optimísticamente
          // Remover de la columna del workflow
          setWorkflowData(prevData => {
            const newData = { ...prevData };
            const fromStage = draggedFicha.workflow_stage;

            if (newData[fromStage]) {
              newData[fromStage] = {
                ...newData[fromStage],
                cases: newData[fromStage].cases.filter(f => f.id !== draggedFicha.id)
              };
            }

            return newData;
          });

          // Agregar a fichas disponibles
          const availableFicha = {
            ...draggedFicha,
            workflow_stage: 'disponible',
            caso_id: null,
            is_available: true
          };
          setFichasDisponibles(prev => [availableFicha, ...prev]);
        }
      }
      // Movimiento dentro del workflow (funcionalidad existente)
      else if (draggedFicha.workflow_stage !== targetStage && targetStage !== 'disponible') {
        updateCaseStage(
          draggedFicha.id,
          draggedFicha.workflow_stage,
          targetStage,
          `Movido via drag & drop a ${casosUtils.getStageName(targetStage)}`
        );
      }
    } catch (error) {
      console.error('❌ Error en drag & drop:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al mover la ficha',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }

    setDraggedFicha(null);
  };

  const handleDragEnd = () => {
    setDraggedFicha(null);
    setDragOverColumn(null);
  };

  /**
   * Abrir modal de detalle de survey response (igual que en pestaña Fichas)
   */
  const handleOpenModalSurveyResponse = (ficha) => {
    console.log('CasoDetailView - handleOpenModalSurveyResponse:', ficha);
    setSurveyModalData(null);
    setIsOpenSurveyResponse(true);

    // Cargar datos de la ficha con manejo de errores
    const fetchSurveyResponse = async () => {
      try {
        const surveyResponse = await getSurveyResponse(ficha.id);
        console.log('CasoDetailView - surveyResponse loaded:', surveyResponse);
        setSurveyModalData(surveyResponse);
      } catch (error) {
        console.error('Error al cargar datos de la ficha:', error);
        setSurveyModalData({ error: 'No se pudieron cargar los datos de la ficha' });
      }
    };

    fetchSurveyResponse();
  };

  const handleCloseModalSurveyResponse = () => {
    setIsOpenSurveyResponse(false);
    setSurveyModalData(null);
  };


  // Calcular total de fichas en el caso
  const totalFichas = Object.values(workflowData).reduce((total, stage) => total + (stage.cases?.length || 0), 0);

  const statusStyle = STATUS_STYLE[caso?.status] || STATUS_STYLE.cerrado;
  const stagePastel = STAGE_PASTEL[caso?.current_stage] || 'sky';

  return (
    <Box w="100%" fontFamily="jakarta">
      {/* Breadcrumb y Header del Caso */}
      <VStack align="stretch" spacing={4} mb={6}>
        {/* Breadcrumb */}
        <HStack spacing={3}>
          <Button
            leftIcon={<Icon as={ChevronLeft} />}
            size="sm" variant="ghost" color="coral.500"
            onClick={onBack || (() => window.history.back())}
            isDisabled={!onBack && window.history.length <= 1}
          >
            Volver a casos
          </Button>
          <Breadcrumb separator=">" fontSize="13px">
            <BreadcrumbItem>
              <BreadcrumbLink color="ink.400">Casos</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink fontWeight="800" color="ink.900">
                {caso?.title || 'Caso sin título'}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </HStack>

        {/* Información del Caso */}
        <Box
          bg="white" border="1px solid" borderColor="cream.line" borderRadius="18px"
          p={4} boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
        >
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <VStack align="start" spacing={1}>
                <Text fontSize="17px" fontWeight="800" color="ink.900">
                  {caso?.title || 'Caso sin título'}
                </Text>
                {caso?.description && (
                  <Text fontSize="13px" color="ink.400">
                    {caso.description}
                  </Text>
                )}
              </VStack>

              <HStack spacing={2}>
                <Box as="span" px="10px" py="3px" borderRadius="999px" bg={statusStyle.bg} color={statusStyle.ink}
                  fontSize="12px" fontWeight="700">
                  {caso?.status_name || casosUtils.getStatusName(caso?.status)}
                </Box>
                <Box as="span" px="10px" py="3px" borderRadius="999px" bg={`${stagePastel}.bg`} color={`${stagePastel}.ink`}
                  fontSize="12px" fontWeight="700">
                  {caso?.stage_name || casosUtils.getStageName(caso?.current_stage)}
                </Box>
              </HStack>
            </HStack>

            {caso && (
              <HStack spacing={4} fontSize="12.5px" color="ink.400" flexWrap="wrap">
                <HStack spacing={1}>
                  <Icon as={Calendar} boxSize={3.5} />
                  <Text>Creado {casosUtils.getTimeAgo(caso.created_at)}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FileText} boxSize={3.5} />
                  <Text>{caso.fichas_count || 0} fichas</Text>
                </HStack>
                {caso.created_by && (
                  <Text>por {caso.created_by}</Text>
                )}
              </HStack>
            )}
          </VStack>
        </Box>
      </VStack>

      {/* Error Boundary */}
      {error && (
        <Box bg="rose.bg" borderRadius="14px" p={3} mb={4}>
          <Text fontSize="12.5px" color="rose.ink" fontWeight="600">{error}</Text>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <VStack spacing={4} py={8}>
          <Spinner size="lg" color="coral.500" thickness="3px" />
          <Text fontSize="13px" color="ink.400">
            Cargando información del caso...
          </Text>
        </VStack>
      )}

      {/* Header del Workflow */}
      {!loading && (
        <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
          <Text fontSize="14.5px" fontWeight="800" color="ink.900">
            Workflow del caso ({totalFichas} fichas)
          </Text>

        <HStack spacing={2}>
          {/* Menú de formularios de casos */}
          {availableCaseForms.length > 0 && (
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<Plus size={16} />}
                size="sm" borderRadius="10px" fontWeight="700" fontSize="12.5px"
                bg="coral.500" color="#fff" _hover={{ bg: 'coral.600' }}
                minW="200px"
              >
                Llenar formularios de Casos
              </MenuButton>
              <Portal>
                <MenuList
                  bg="white" borderColor="cream.line" fontFamily="jakarta"
                  maxW="300px" shadow="xl" zIndex={99999} border="1px solid" borderRadius="12px"
                >
                  {availableCaseForms.map((form) => (
                    <MenuItem
                      key={form.id}
                      onClick={() => {
                        if (onAddFicha) {
                          onAddFicha('casos', form.id, caso.id);
                        }
                      }}
                      _hover={{ bg: 'coral.tint' }}
                      _focus={{ bg: 'coral.tint' }}
                      p={3}
                    >
                      <VStack spacing={1} align="flex-start" w="full">
                        <Text fontSize="13px" fontWeight="700" color="ink.900" noOfLines={2}>
                          {form.name}
                        </Text>
                        {form.description && (
                          <Text fontSize="11.5px" color="ink.400" noOfLines={2}>
                            {form.description}
                          </Text>
                        )}
                      </VStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}

          {/* Botón fallback si no hay formularios */}
          {availableCaseForms.length === 0 && (
            <Button
              size="sm" borderRadius="10px" fontWeight="700" fontSize="12.5px"
              leftIcon={<Plus size={14} />}
              onClick={() => {
                if (onAddFicha) {
                  onAddFicha();
                }
              }}
              bg="cream.bg2" color="ink.400"
              isDisabled
            >
              Sin formularios de casos
            </Button>
          )}

          <Button
            size="sm" borderRadius="10px" fontWeight="700" fontSize="12.5px"
            leftIcon={<RefreshCw size={16} />}
            onClick={() => {
              if (onRefresh) {
                onRefresh(); // Refrescar datos desde el perfil
              } else {
                // Fallback: recargar datos del caso
                loadFichasDelCaso();
                loadFichasDisponibles();
              }
            }}
            border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            isLoading={loading}
            minW="120px"
          >
            Refrescar
          </Button>
        </HStack>
      </HStack>
      )}

      {/* Workflow Columns */}
      {!loading && (
      <Box
        overflowX="auto"
        overflowY="hidden"
        w="100%"
        maxW="100%"
        border="1px solid"
        borderColor="cream.line"
        borderRadius="20px"
        p={4}
        bg="cream.bg2"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
        position="relative"
        css={{
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F4EEE7',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E6DBD0',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#EF5A2A',
          },
        }}
      >
        <Flex
          gap={5}
          minWidth="1800px"
          w="max-content"
          py={2}
          px={1}
          position="relative"
        >
          {/* Columna 1: Fichas Disponibles */}
          <FichasDisponiblesColumn
            fichas={fichasDisponibles}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            isDragOver={dragOverColumn === 'disponible'}
            draggedFicha={draggedFicha}
            onShowDetail={handleOpenModalSurveyResponse}
          />

          {/* Columnas 2-5: Workflow Stages */}
          {Object.keys(casosUtils.CASO_WORKFLOW_STAGES).map((stageKey) => {
            const stageData = workflowData[stageKey] || {
              stage: stageKey,
              name: casosUtils.CASO_WORKFLOW_STAGES[stageKey],
              color: casosUtils.getStageColor(stageKey),
              cases: []
            };
            const isDropTarget = dragOverColumn === stageKey;

            return (
              <WorkflowColumn
                key={stageKey}
                stage={stageData}
                isDropTarget={isDropTarget}
                onDragOver={(e) => handleDragOver(e, stageKey)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stageKey)}
              >
                {stageData.cases.map((caso) => (
                  <CasoCard
                    key={caso.id}
                    caso={caso}
                    isDragging={draggedFicha?.id === caso.id}
                    onDragStart={(e) => handleDragStart(e, caso)}
                    onDragEnd={handleDragEnd}
                    onMoveToStage={updateCaseStage}
                    onCaseClick={onFichaClick}
                    onShowDetail={handleOpenModalSurveyResponse}
                  />
                ))}
              </WorkflowColumn>
            );
          })}
        </Flex>
      </Box>
      )}

      {/* Modal de Detalle de Survey Response - Igual que en pestaña Fichas */}
      <ModalSurveyResponse
        isOpen={isOpenSurveyResponse}
        onClose={handleCloseModalSurveyResponse}
        customer={customer}
        data={surveyModalData}
        onSurveyDeleted={() => {
          // Actualizar datos después de eliminar survey
          loadFichasDelCaso();
          loadFichasDisponibles();
        }}
      />
    </Box>
  );
};

/**
 * Componente de columna del workflow
 */
const STAGE_HEADER_PASTEL = {
  recepcion: 'sky',
  investigacion: 'lav',
  resolucion: 'butter',
  cierre: 'mint',
};

const WorkflowColumn = ({
  stage,
  children,
  isDropTarget,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const pastel = STAGE_HEADER_PASTEL[stage.stage] || 'sky';

  return (
    <Box
      flex="0 0 290px"
      minW="290px"
      maxW="290px"
      bg="white"
      borderRadius="16px"
      border="1.5px solid"
      borderColor={isDropTarget ? 'coral.500' : 'cream.line'}
      boxShadow={isDropTarget ? '0 0 0 3px rgba(239,90,42,.15)' : '0 2px 8px -2px rgba(35,30,26,.06)'}
      transition="border-color .15s, box-shadow .15s"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      position="relative"
      overflow="hidden"
    >
      {/* Header de la columna */}
      <Box p={3} bg={`${pastel}.bg`} borderTopRadius="16px">
        <VStack spacing={0}>
          <Text fontSize="13.5px" fontWeight="800" textAlign="center" color={`${pastel}.ink`}>
            {stage.name}
          </Text>
          <Text fontSize="10px" textAlign="center" color={`${pastel}.ink`} opacity={0.75} fontWeight="700" letterSpacing="0.05em">
            {stage.stage?.toUpperCase()}
          </Text>
        </VStack>
      </Box>

      {/* Contenido de la columna */}
      <VStack
        spacing={2}
        p={2.5}
        align="stretch"
        minH="300px"
        maxH="400px"
        overflowY="auto"
      >
        {children}

        {/* Mensaje cuando no hay casos */}
        {(!stage.cases || stage.cases.length === 0) && (
          <Box textAlign="center" color="ink.400" py={4} fontSize="12.5px">
            <Text fontWeight="600">Vacío</Text>
            <Text fontSize="11px" mt={1}>
              Arrastra casos aquí
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

/**
 * Componente de tarjeta de caso
 */
const CasoCard = ({
  caso,
  isDragging,
  onDragStart,
  onDragEnd,
  onMoveToStage,
  onCaseClick,
  onShowDetail
}) => {
  const opacity = isDragging ? 0.5 : 1;

  // Obtener stages siguientes y anteriores
  const currentStage = caso.workflow_stage;
  const nextStage = casosUtils.getNextStage(currentStage);
  const prevStage = casosUtils.getPreviousStage(currentStage);

  const handleMoveToNext = () => {
    if (nextStage) {
      onMoveToStage(caso.id, currentStage, nextStage, 'Avanzado al siguiente stage');
    }
  };

  const handleMoveToPrev = () => {
    if (prevStage) {
      onMoveToStage(caso.id, currentStage, prevStage, 'Retrocedido al stage anterior');
    }
  };

  return (
    <Box
      bg="white"
      border="1px solid" borderColor="cream.line"
      borderRadius="12px"
      p={3}
      opacity={opacity}
      cursor="grab"
      transition="background .13s, border-color .13s, transform .13s"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      _hover={{
        bg: 'coral.tint', borderColor: 'coral.soft',
        transform: 'translateY(-2px)'
      }}
      _active={{ cursor: 'grabbing' }}
    >
      {/* Header compacto con título y indicador de stage */}
      <HStack justify="space-between" align="flex-start" mb={2}>
        <Text fontSize="13px" fontWeight="700" color="ink.900" noOfLines={2} flex={1}>
          {caso.survey_name}
        </Text>
        <Box as="span" px="7px" py="2px" borderRadius="999px" bg="sky.bg" color="sky.ink"
          fontSize="9.5px" fontWeight="700" flexShrink={0} ml={2}>
          {caso.stage_name || casosUtils.getStageName(caso.workflow_stage) || 'Sin stage'}
        </Box>
      </HStack>

      {/* Metadata compacta */}
      <VStack spacing={0.5} align="stretch" mb={2}>
        <HStack fontSize="11px" color="ink.400" justify="space-between">
          <HStack spacing={1}>
            <Calendar size={10} />
            <Text>{casosUtils.formatDate(caso.created_at)}</Text>
          </HStack>
          <Text fontSize="11px" color="ink.400">#{caso.id}</Text>
        </HStack>

        {caso.stage_updated_at && (
          <HStack fontSize="11px" color="ink.400">
            <Clock size={10} />
            <Text>{casosUtils.getTimeAgo(caso.stage_updated_at)}</Text>
          </HStack>
        )}
      </VStack>

      {/* Botones de acción compactos */}
      <HStack mt={2} justify="space-between" spacing={1}>
        {/* Navegación de stages */}
        <HStack spacing={1}>
          <Tooltip label={prevStage ? `← ${casosUtils.CASO_WORKFLOW_STAGES[prevStage]}` : 'Primer stage'}>
            <IconButton
              icon={<ArrowLeft size={10} />}
              size="xs" variant="ghost" color="ink.400"
              _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
              isDisabled={!prevStage}
              onClick={handleMoveToPrev}
            />
          </Tooltip>

          <Tooltip label={nextStage ? `${casosUtils.CASO_WORKFLOW_STAGES[nextStage]} →` : 'Último stage'}>
            <IconButton
              icon={<ArrowRight size={10} />}
              size="xs" variant="ghost" color="ink.400"
              _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
              isDisabled={!nextStage}
              onClick={handleMoveToNext}
            />
          </Tooltip>
        </HStack>

        {/* Botón ver detalles */}
        <Tooltip label="Ver detalles del caso" hasArrow>
          <IconButton
            icon={<Eye size={14} />}
            size="sm" minW="32px" h="32px" borderRadius="9px"
            bg="coral.500" color="#fff"
            _hover={{ bg: 'coral.600', transform: 'scale(1.05)' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onCaseClick) {
                onCaseClick(caso); // Usar onCaseClick si está disponible (comportamiento original)
              } else if (onShowDetail) {
                onShowDetail(caso); // Abrir modal de detalle
              }
            }}
          />
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default CasoDetailView;